import { Capacitor } from '@capacitor/core';
import { env } from '@config/env';
import type { RuntimeDevConfig } from '@config/env/types';
import { useAuthStore } from '../stores/authStore';
import {
  RESOLVED_IS_ROOT_TENANT_KEY,
  RESOLVED_ROOT_TENANT_ID_KEY,
} from './ssoRootScope';

declare global {
  interface Window {
    __IP4X_DEV__?: RuntimeDevConfig;
  }
}

function isLocalhost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('localhost:') ||
    hostname.startsWith('127.0.0.1:')
  );
}

/** Runtime overrides from public/runtime-env.js (optional localhost dev). */
export function readRuntimeDevConfig(): Window['__IP4X_DEV__'] {
  if (typeof window === 'undefined') return undefined;
  return window.__IP4X_DEV__;
}

/** True when localhost should use Tenant API /auth/login instead of Core portal-auth. */
export function shouldUseDirectTenantLogin(): boolean {
  if (env.authMode === 'tenant') return true;
  const runtime = readRuntimeDevConfig();
  if (runtime?.directTenantLogin) return true;
  if (env.localDevDirectTenant) return true;
  return false;
}

/**
 * Pin single-tenant localhost dev: tenant id, regional API URL, and clear
 * stale SSO / hub domain from other verticals.
 *
 * IMPORTANT — native guard. A native Capacitor app is served from
 * `capacitor://localhost`, so `window.location.hostname === 'localhost'` even
 * though this is a production build, not localhost browser dev. Without the
 * guard below this routine runs on every boot AND every login of the native
 * SSO build and:
 *   - removes `paas_sso_tenants` → the Communities switcher goes empty, and
 *   - overwrites `paas_active_portal_domain` with the hub `defaultDomain` →
 *     the next reopen re-resolves the hub and reverts the selected community's
 *     branding back to the SSO hub.
 * That is exactly the "branding + tenants lost on reopen" bug. On native we
 * therefore only apply the pin for an explicit single-tenant (direct-tenant)
 * build that actually wants it; an SSO build skips it entirely. Real localhost
 * browser dev is unchanged.
 */
export function applyLocalDevBootstrap(): void {
  if (typeof window === 'undefined') return;
  const hostname = window.location.hostname;
  const runtime = readRuntimeDevConfig();
  const directTenantLogin = shouldUseDirectTenantLogin();

  if (Capacitor.isNativePlatform()) {
    if (!directTenantLogin) return;
  } else if (!isLocalhost(hostname)) {
    return;
  }

  const tenantId = (runtime?.defaultTenantId || env.localDevTenantId || '').trim();
  const apiUrl = env.apiBaseUrl;
  const targetDomain = (runtime?.defaultDomain || env.defaultDomain || '').trim();
  const shouldPinSingleTenantContext = directTenantLogin;

  // Core/SOO localhost dev should preserve the resident's active community and
  // linked-tenant state across refreshes. Only explicit single-tenant pinning
  // should rewrite tenant context or clear SSO data.
  if (!shouldPinSingleTenantContext) {
    return;
  }

  // Seed the dev tenant, never override a chosen one. A resident who picked a community — or
  // just signed up into one — has an account that exists ONLY in that tenant's database, and
  // re-pinning the dev default here sends their login to the hub, which 401s on a valid password.
  if (tenantId && !useAuthStore.getState().tenantId) {
    useAuthStore.getState().setTenantId(tenantId);
    useAuthStore.getState().setRegionalApiUrl(apiUrl);
    try {
      localStorage.setItem('paas_tenant_id', tenantId);
      localStorage.setItem('paas_regional_api_url', apiUrl);
    } catch {
      /* storage unavailable */
    }
  }

  try {
    localStorage.removeItem('paas_sso_session_token');
    localStorage.removeItem('paas_sso_tenants');
    localStorage.removeItem(RESOLVED_IS_ROOT_TENANT_KEY);
    localStorage.removeItem(RESOLVED_ROOT_TENANT_ID_KEY);
    if (targetDomain) {
      localStorage.setItem('paas_active_portal_domain', targetDomain);
    } else {
      localStorage.removeItem('paas_active_portal_domain');
    }
  } catch {
    /* noop */
  }
}
